import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Field, FieldLabel, FieldDescription } from "@lawline/ui/components/field";
import { Textarea } from "@lawline/ui/components/textarea";

import { useAuth } from "@platform/auth/context";
import { saveContextMutation } from "../memory.mutations";
import { memoryContextQueryOptions } from "../memory.queries";

export function MemoryContext() {
	const t = useTranslations("memory.context");
	const { can } = useAuth();
	const { data } = useQuery(memoryContextQueryOptions);
	const save = useMutation(saveContextMutation);

	const canWrite = can({ memory: ["write"] });
	const saved = data?.context ?? "";
	const [draft, setDraft] = useState(saved);

	useEffect(() => setDraft(saved), [saved]);

	const dirty = draft !== saved;

	return (
		<section className="flex flex-col gap-3">
			<Field>
				<FieldLabel className="text-lg">{t("title")}</FieldLabel>
				<FieldDescription>{t("description")}</FieldDescription>
				<Textarea
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					placeholder={t("placeholder")}
					disabled={!canWrite || save.isPending}
					rows={8}
				/>
			</Field>
			<div className="flex items-center gap-4">
				{canWrite && (
					<Button
						size="sm"
						className="self-start"
						disabled={!dirty || save.isPending}
						onClick={() => save.mutate(draft)}
					>
						{save.isPending ? t("saving") : t("save")}
					</Button>
				)}
				{save.error && (
					<p className="text-destructive text-sm">{save.error.message || t("error")}</p>
				)}
			</div>
		</section>
	);
}
